import { Link, useLocation, useNavigate } from "react-router-dom";
import { Logo, LogoMark } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useQuote } from "@/hooks/usePolling";
import { DataStatusBadge } from "@/components/common";
import { MarketStatusBadge, PlanBadge } from "@/components/app/UpgradeGate";
import { NotificationCenter } from "@/components/app/NotificationCenter";
import {
  LayoutDashboard, BookOpen, BarChart3, MessageSquare, Sparkles,
  GraduationCap, Settings, Sun, Moon, LogOut, Shield, FlaskConical, Newspaper, LineChart
} from "lucide-react";

const NAV = [
  { to: "/app", label: "Terminal", icon: LayoutDashboard, testId: "nav-terminal" },
  { to: "/app/brief", label: "Daily Brief", icon: Newspaper, testId: "nav-brief" },
  { to: "/app/journal", label: "Journal", icon: BookOpen, testId: "nav-journal" },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3, testId: "nav-analytics" },
  { to: "/app/backtest", label: "Backtesting", icon: LineChart, testId: "nav-backtest" },
  { to: "/app/ask", label: "Ask Aurum", icon: MessageSquare, testId: "nav-ask" },
  { to: "/app/coach", label: "AI Coach", icon: Sparkles, testId: "nav-coach" },
  { to: "/app/strategy", label: "Strategy Lab", icon: FlaskConical, testId: "nav-strategy" },
  { to: "/app/learn", label: "Learn", icon: GraduationCap, testId: "nav-learn-app" },
  { to: "/app/settings", label: "Settings", icon: Settings, testId: "nav-settings" },
];

export function AppShell({ children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout, plan, ent } = useAuth();
  const { theme, toggle } = useTheme();
  const { data: quote } = useQuote(4000);

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-60 shrink-0 flex-col border-r border-[var(--border2)] bg-[var(--bg-elev)] sticky top-0 h-screen">
        <div className="h-16 flex items-center px-5 border-b border-[var(--border2)]">
          <Link to="/"><Logo size={26} /></Link>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map((n) => {
            const active = loc.pathname === n.to;
            const Icon = n.icon;
            return (
              <Link key={n.to} to={n.to} data-testid={n.testId}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                  active ? "bg-[var(--surface)] text-[var(--text)] font-medium" : "text-[var(--text-sec)] hover:text-[var(--text)] hover:bg-[var(--surface)]"
                }`}>
                <Icon size={17} className={active ? "text-aurum" : ""} />
                {n.label}
                {active && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-aurum" />}
              </Link>
            );
          })}
          {user?.role === "admin" && (
            <Link to="/app/admin" data-testid="nav-admin"
              className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                loc.pathname === "/app/admin" ? "bg-[var(--surface)] text-aurum font-medium" : "text-[var(--text-sec)] hover:text-[var(--text)] hover:bg-[var(--surface)]"
              }`}>
              <Shield size={17} /> Admin
            </Link>
          )}
        </nav>
        <div className="p-3 border-t border-[var(--border2)]">
          <div className="flex items-center gap-2 px-2 py-2">
            <div className="h-8 w-8 rounded-full btn-gold flex items-center justify-center text-xs font-bold">
              {(user?.name || "U").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm truncate">{user?.name}</div>
              <div className="mt-0.5"><PlanBadge plan={plan} /></div>
            </div>
            <button onClick={() => { logout(); nav("/"); }} data-testid="logout-btn"
              className="ml-auto p-1.5 rounded-md text-[var(--text-muted)] hover:text-bear hover:bg-[var(--surface)]" aria-label="Log out">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="h-16 sticky top-0 z-40 glass border-b border-[var(--border2)] flex items-center px-4 sm:px-6 gap-4">
          <Link to="/app" className="lg:hidden"><LogoMark size={26} /></Link>
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold">XAU/USD</span>
            {quote && (
              <>
                <span className="tnum text-lg font-semibold" data-testid="topbar-price">
                  ${quote.price?.toFixed(2)}
                </span>
                <span className={`tnum text-xs font-medium ${quote.change >= 0 ? "text-bull" : "text-bear"}`}>
                  {quote.change >= 0 ? "▲" : "▼"} {Math.abs(quote.change).toFixed(2)} ({quote.change_pct}%)
                </span>
                <span className="hidden sm:inline text-[11px] text-[var(--text-muted)] tnum">spread {quote.spread}</span>
              </>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {quote && <MarketStatusBadge marketOpen={quote.market_open} />}
            {quote && <DataStatusBadge status={quote.data_status} />}
            <NotificationCenter />
            <button onClick={toggle} className="p-2 rounded-lg text-[var(--text-sec)] hover:text-[var(--text)] hover:bg-[var(--surface)]" aria-label="Theme" data-testid="app-theme-toggle">
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
        </header>

        {/* Mobile bottom nav */}
        <MobileNav />

        <TrialBanner ent={ent} />
        <main className="flex-1 p-4 sm:p-6 pb-24 lg:pb-6">{children}</main>
      </div>
    </div>
  );
}
function TrialBanner({ ent }) {
  if (!ent?.trial_ends_at) return null;
  const ends = new Date(ent.trial_ends_at).getTime();
  const now = Date.now();
  if (isNaN(ends) || ends <= now) return null;
  const days = Math.ceil((ends - now) / 86400000);
  return (
    <div data-testid="trial-banner"
      className="mx-4 sm:mx-6 mt-4 rounded-xl px-4 py-2.5 flex items-center gap-3 text-sm"
      style={{ background: "rgba(214,174,87,0.1)", border: "1px solid rgba(214,174,87,0.3)" }}>
      <Sparkles size={16} className="text-aurum shrink-0" />
      <span className="text-[var(--text-sec)]">
        <span className="font-semibold text-[var(--text)]">{days} day{days === 1 ? "" : "s"} left</span> in your Pro free trial — you'll be billed automatically when it ends.
      </span>
      <Link to="/pricing" data-testid="trial-manage" className="ml-auto shrink-0 text-xs font-semibold text-aurum hover:underline">Manage plan</Link>
    </div>
  );
}



function MobileNav() {
  const loc = useLocation();
  const items = NAV.slice(0, 5);
  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 glass border-t border-[var(--border2)] flex justify-around py-2">
      {items.map((n) => {
        const Icon = n.icon;
        const active = loc.pathname === n.to;
        return (
          <Link key={n.to} to={n.to} className={`flex flex-col items-center gap-0.5 px-2 ${active ? "text-aurum" : "text-[var(--text-muted)]"}`}>
            <Icon size={19} />
            <span className="text-[10px]">{n.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
